/**
 * DOCUMENTATION:
 * Component: GCPNetworking
 * Purpose: Deep dive into VPCs, Shared VPCs, Peering, and Hybrid Connectivity.
 * * * Note for Junior Developers:
 * - Global VPC: A single VPC in GCP can span multiple regions.
 * - Subnets: While the VPC is global, subnets are REGIONAL.
 * - Shared VPC: Allows an organization to connect resources from multiple projects
 * to a common VPC network (Centralized Control).
 * - VPC Peering: Connects two VPC networks so that resources in each network
 * can communicate (Decentralized Control).
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
    Alert, Divider, Tooltip
} from "@mui/material";

/* ✅ Math Rendering */
import 'katex/dist/katex.min.css';
import { BlockMath } from 'react-katex';

/* Icons */
import RouterIcon from '@mui/icons-material/Router';
import SecurityIcon from '@mui/icons-material/Security';
import HubIcon from '@mui/icons-material/Hub';
import SwapCallsIcon from '@mui/icons-material/SwapCalls';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import LanguageIcon from '@mui/icons-material/Language';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a192d",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(25, 40, 45, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    border: "1px solid rgba(66, 133, 244, 0.15)",
    color: "white",
}));

const TechChip = styled(Box)(({ theme, color }) => ({
    padding: "4px 12px",
    borderRadius: "20px",
    border: `1px solid ${color || '#4285F4'}`,
    color: color || '#4285F4',
    fontSize: "0.75rem",
    fontWeight: 700,
    backgroundColor: "rgba(255,255,255,0.05)"
}));

export default function GCPNetworking() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography variant={isMobile ? "h4" : "h2"} fontWeight={900} color="#4285F4">
                    VPC DEEP DIVE
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.6 }}>The Global Networking Backbone</Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>
                {/* 1. Global VPC Concept */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <LanguageIcon sx={{ color: "#4285F4", fontSize: 40 }} />
                            <Typography variant="h5" fontWeight={800}>Global Infrastructure</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, opacity: 0.9 }}>
                            In Google Cloud, the **VPC (Virtual Private Cloud)** is a global resource. It is not tied to a specific region.
                            However, the **Subnets** you create inside that VPC are regional. This allows for a "Hub and Spoke"
                            architecture where different regions can communicate over Google's private fiber without crossing the public internet.
                        </Typography>



                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px", borderLeft: "4px solid #4285F4" }}>
                                    <Typography variant="subtitle2" color="#4285F4">Auto Mode VPC</Typography>
                                    <Typography variant="caption">Creates subnets in every region automatically. (Not for Production).</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px", borderLeft: "4px solid #34A853" }}>
                                    <Typography variant="subtitle2" color="#34A853">Custom Mode VPC</Typography>
                                    <Typography variant="caption">You define specific subnets and IP ranges. (Recommended for PCA Exam).</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px", borderLeft: "4px solid #FBBC04" }}>
                                    <Typography variant="subtitle2" color="#FBBC04">Firewall Rules</Typography>
                                    <Typography variant="caption">Implicit Deny All Ingress / Allow All Egress. Rules are stateful.</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 2. Shared VPC vs. Peering */}
                <Grid item xs={12} md={8}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <HubIcon sx={{ color: "#EA4335" }} />
                            <Typography variant="h5" fontWeight={800}>Connecting Networks</Typography>
                        </Stack>



                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="#4285F4" gutterBottom>Shared VPC</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                                    Ideal for **Centralized Governance**. One "Host Project" manages the network, and
                                    "Service Projects" use its subnets.
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <TechChip color="#4285F4">Centralized</TechChip>
                                    <TechChip color="#4285F4">Large Enterprise</TechChip>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="#34A853" gutterBottom>VPC Peering</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                                    Ideal for **Decentralized Ecosystems**. Connects two VPCs as if they were one, but
                                    each VPC retains its own administration.
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <TechChip color="#34A853">Decentralized</TechChip>
                                    <TechChip color="#34A853">SaaS Partners</TechChip>
                                </Stack>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 3. Hybrid Connectivity & Security */}
                <Grid item xs={12} md={4}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <SecurityIcon sx={{ color: "#FBBC04" }} />
                            <Typography variant="h5" fontWeight={800}>Hybrid & Edge</Typography>
                        </Stack>

                        <Stack spacing={2}>
                            <Box sx={{ p: 2, bgcolor: "rgba(0,0,0,0.2)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2" color="#FBBC04">Cloud Interconnect</Typography>
                                <Typography variant="caption">Dedicated physical link between your Data Center and Google. (Highest Speed).</Typography>
                            </Box>
                            <Box sx={{ p: 2, bgcolor: "rgba(0,0,0,0.2)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2" color="#FBBC04">Cloud VPN</Typography>
                                <Typography variant="caption">Connects via IPSec over public internet. (Fast setup, lower cost).</Typography>
                            </Box>
                            <Box sx={{ p: 2, bgcolor: "rgba(0,0,0,0.2)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2" color="#FBBC04">Cloud Armor</Typography>
                                <Typography variant="caption">WAF / DDoS protection for Global Load Balancers.</Typography>
                            </Box>
                        </Stack>


                    </SectionCard>
                </Grid>

                {/* Exam Alert */}
                <Grid item xs={12}>
                    <Alert severity="warning" sx={{ bgcolor: "rgba(234, 67, 53, 0.1)", color: "white", border: "1px solid #EA4335" }}>
                        <strong>PCA SCENARIO:</strong> If the client requires 10Gbps+ constant throughput and low latency between On-Prem and GCP, always choose **Dedicated Interconnect**. If they only have a few users and want to save money, choose **Cloud VPN**.
                    </Alert>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/gcp-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#4285F4", color: "white", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
